import { handleActions } from 'redux-actions';
import { Actions, getUserFailure, getUserSuccess, getUserRequest, createUserSuccess, getUsersSuccess, getUsersRequest } from 'actions/userActions';
import { Actions as BuildingActions } from 'actions/buildingActions';
import { Actions as SessionActions, loginSuccess } from 'actions/sessionActions';
import { User, UserId } from 'shared/models';
import { filter, omit } from 'lodash';

export type State = {
    data: Record<UserId, User>;
    loaded: boolean;
    loading: Record<UserId, boolean>;
    lastSearch: {
        ref?: string; // Random ID to pair requests to subsequent responses (e.g. to determine if result batches are from the same request)
        order: string[];
        more: boolean;
    };
    error: string | null;
};

export const initialState = Object.freeze<State>({
    data: {},
    loaded: false,
    loading: {},
    lastSearch: {
        order: [],
        more: false,
    },
    error: null,
});


// Helper method to add an array of users into the given state
const addUsers = (state: State, users: readonly User[]): State => ({
    ...state,
    data: users.reduce((map, u) => ({ ...map, [u.id]: u }), state.data),
});

export const usersReducer = handleActions({
    [Actions.CREATE_USER_SUCCESS]: (state: State, action: ReturnType<typeof createUserSuccess>) => {
        const user: User = action.payload;
        return {
            ...addUsers(state, [ user ]),
            lastSearch: {
                ...state.lastSearch,
                order: [
                    user.id,
                    ...(state.lastSearch.order || []),
                ],
            },
        };
    },


    [Actions.GET_USERS_REQUEST]: (state: State, action: ReturnType<typeof getUsersRequest>) => {
        const { ref } = action.meta;
        return {
            ...state,
            loading: {
                ...state.loading,
                all: true,
            },
            lastSearch: {
                ...state.lastSearch,
                ref,
            },
        };
    },
    [Actions.GET_USERS_SUCCESS]: (state: State, action: ReturnType<typeof getUsersSuccess>) => {
        const users = action.payload;
        const { more, ref } = action.meta;

        // Make no changes if the results we just received were from a stale request
        if (ref !== state.lastSearch.ref) {
            return state;
        }
        const order = users.map((u: User) => u.id);
        const append = state.lastSearch.more;
        return {
            ...addUsers(state, users),
            error: null,
            loaded: true,
            loading: omit(state.loading, 'all'),
            lastSearch: {
                ...state.lastSearch,
                // When requesting more, append to the previous order of user IDs
                order: append ? [ ...state.lastSearch.order, ...order ] : order,
                more,
            },
        };
    },
    [Actions.GET_USERS_FAILURE]: (state: State, action: any) => {
        // Make no changes if the results we just received were from a stale request
        const { ref } = action.meta;
        if (ref !== state.lastSearch.ref) {
            return state;
        }
        return {
            ...state,
            error: action.payload.message,
            loading: omit(state.loading, 'all'),
        };
    },


    [Actions.GET_USER_REQUEST]: (state: State, action: ReturnType<typeof getUserRequest>) => {
        const userId = action.payload;
        return {
            ...state,
            loading: {
                ...state.loading,
                [userId]: true,
            },
        };
    },
    [Actions.GET_USER_SUCCESS]: (state: State, action: ReturnType<typeof getUserSuccess>) => {
        const user = action.payload;
        return {
            ...state,
            loading: omit(state.loading, user.id),
            data: {
                ...state.data,
                [user.id]: user,
            },
        };
    },
    [Actions.GET_USER_FAILURE]: (state: State, action: ReturnType<typeof getUserFailure>) => {
        const { userId } = action.meta;
        return {
            ...state,
            loading: omit(state.loading, userId),
            error: action.payload.message,
        };
    },


    [Actions.UPDATE_USER_SUCCESS]: (state: State, action: any) => {
        const { payload: user } = action;
        return {
            ...state,
            data: {
                ...state.data,
                [user.id]: user,
            },
        };
    },


    //[Actions.DELETE_USER_REQUEST]: (state: State, action: any) => {
    //    return state;
    //},
    [Actions.DELETE_USER_SUCCESS]: (state: State, action: any) => {
        const { id } = action.meta;
        return {
            ...state,
            data: omit(state.data, id),
            lastSearch: {
                ...state.lastSearch,
                order: filter(state.lastSearch.order, orderId => orderId !== id),
            },
        };
    },
    [Actions.DELETE_USER_FAILURE]: (state: State, action: any) => {
        return { ...state, error: action.payload.message };
    },


    //
    //  Building Actions
    //
    [BuildingActions.GET_BUILDING_SUCCESS]: (state: State, action: any) => {
        const { /* building, devices, themes,*/ users } = action.payload;
        return addUsers(state, users);
    },


    //
    //  Session Actions
    //
    [SessionActions.LOGIN_SUCCESS]: (state: State, action: ReturnType<typeof loginSuccess>) => {
        // When user logs in, their (almost) full user record is returned.
        // So stash it in the users list.
        const { user } = action.payload;
        return addUsers(state, [ user ]);
    },

} as any, initialState);
